"use client";
import React from "react";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import Image from "@/helpers/Image";
import humanDeco from "@/public/decos/human-deco.svg";

/**
 * Node graph for the Emotions section (home). Forked from
 * `components/global/ui/ConnectionNodes` (which stays untouched for the
 * scroll choreography on /homepage-old) and trimmed to what the static
 * section needs: staggered intro, idle float, repulsion, drag, hover /
 * tap highlight and a description tooltip.
 *
 * Visual per Figma: every node is an 8px white square dot with its label
 * chip 12px below. Lines connect dot to dot. The center node is a dot
 * with a white box holding the human icon.
 */

export interface EmotionNodeData {
  id: string;
  label: string;
  /** Dot center, percentage of the graph box (0–100) */
  x: number;
  y: number;
  connections: string[];
  description: string;
}

export interface EmotionNodesProps {
  nodes: EmotionNodeData[];
  /** Id of the node that sits at the center and anchors the intro spread */
  centerId?: string;
  /** Box aspect ratio, e.g. "1920 / 646" */
  aspectRatio: string;
  /** Starts the intro spread */
  revealed?: boolean;
  /** Tap-to-select mode with container-aware tooltip placement (mobile) */
  compact?: boolean;
  className?: string;
}

// ─── Animation constants ─────────────────────────────────────────────────────

const INTRO_DURATION_S = 0.7;
const INTRO_STAGGER_S = 0.05;
const INTRO_HOLD_S = 0.1;
const FLOAT_RAMP_S = 0.6;
const REPEL_RADIUS = 100;
const REPEL_STRENGTH = 80;
const FRICTION = 0.92;
const FLOAT_AMP = 8;
/** Pointer travel (px) under which a press counts as a tap */
const TAP_SLOP_PX = 6;
/** Dot is 1rem tall; the anchor sits at its center */
const DOT_CENTER_OFFSET = "0.5rem";

function remPx() {
  return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}
function easeOutQuart(t: number) {
  return 1 - Math.pow(1 - t, 4);
}
function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}
function easeOutBack(t: number, s = 1.1) {
  const c3 = s + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + s * Math.pow(t - 1, 2);
}

interface NodeState {
  dragDx: number;
  dragDy: number;
  vx: number;
  vy: number;
  repelDx: number;
  repelDy: number;
  floatPhase: number;
  floatSpeed: number;
  introProgress: number;
  introOpacityProgress: number;
  introScaleProgress: number;
  introDelay: number;
  introStartTime: number;
}

function buildGraph(nodes: EmotionNodeData[], centerId: string) {
  const map = new Map(nodes.map((n) => [n.id, n]));
  const seen = new Set<string>();
  const edges: [string, string][] = [];
  for (const node of nodes) {
    for (const connId of node.connections) {
      if (!map.has(connId)) continue;
      const key = [node.id, connId].sort().join("__");
      if (seen.has(key)) continue;
      seen.add(key);
      edges.push([node.id, connId]);
    }
  }
  const center = map.get(centerId) ?? { x: 50, y: 50 };
  // Clockwise order from 12 o'clock so the intro reads as a wave
  const satellites = nodes
    .filter((n) => n.id !== centerId)
    .map((n) => {
      let a = Math.atan2(n.x - center.x, -(n.y - center.y));
      if (a < 0) a += Math.PI * 2;
      return { id: n.id, a };
    })
    .sort((a, b) => a.a - b.a);
  const order = new Map<string, number>([[centerId, -1]]);
  satellites.forEach((s, i) => order.set(s.id, i));

  const states = new Map<string, NodeState>();
  nodes.forEach((n, i) => {
    const idx = order.get(n.id) ?? 0;
    states.set(n.id, {
      dragDx: 0,
      dragDy: 0,
      vx: 0,
      vy: 0,
      repelDx: 0,
      repelDy: 0,
      floatPhase: (i / nodes.length) * Math.PI * 2,
      floatSpeed: 0.5 + ((i * 7) % 5) * 0.08,
      introProgress: 0,
      introOpacityProgress: 0,
      introScaleProgress: 0,
      introDelay: n.id === centerId ? 0 : INTRO_HOLD_S + idx * INTRO_STAGGER_S,
      introStartTime: -1,
    });
  });
  return { map, edges, states, center };
}

export default function EmotionNodes({
  nodes,
  centerId = "human",
  aspectRatio,
  revealed = false,
  compact = false,
  className,
}: EmotionNodesProps) {
  const graph = React.useMemo(() => buildGraph(nodes, centerId), [nodes, centerId]);
  const graphRef = React.useRef(graph);
  graphRef.current = graph;

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const nodesRef = React.useRef<Map<string, HTMLElement>>(new Map());
  const linesRef = React.useRef<Map<string, SVGLineElement>>(new Map());
  const layoutRef = React.useRef({ width: 0, height: 0 });
  const renderedOffsetsRef = React.useRef<Map<string, { dx: number; dy: number }>>(new Map());

  const [activeNode, setActiveNode] = React.useState<string | null>(null);
  const activeNodeRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    activeNodeRef.current = activeNode;
  }, [activeNode]);

  // Intro start stamp
  const revealedTriggeredRef = React.useRef(false);
  React.useEffect(() => {
    if (!revealed || revealedTriggeredRef.current) return;
    revealedTriggeredRef.current = true;
    const now = performance.now() / 1000;
    for (const s of graphRef.current.states.values()) s.introStartTime = now;
  }, [revealed]);

  // Drag bookkeeping
  const dragRef = React.useRef<{
    nodeId: string;
    pointerId: number;
    pointerType: string;
    captureTarget: HTMLElement;
    startX: number;
    startY: number;
    pointerX: number;
    pointerY: number;
    grabOffsetX: number;
    grabOffsetY: number;
    containerLeft: number;
    containerTop: number;
    moved: boolean;
  } | null>(null);
  const [draggingId, setDraggingId] = React.useState<string | null>(null);
  const draggingIdRef = React.useRef<string | null>(null);

  const handlePointerDown = React.useCallback((e: React.PointerEvent<HTMLButtonElement>, nodeId: string) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const nodeRect = e.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    dragRef.current = {
      nodeId,
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      captureTarget: e.currentTarget,
      startX: e.clientX,
      startY: e.clientY,
      pointerX: e.clientX,
      pointerY: e.clientY,
      // Anchor is the dot center: horizontally centered, DOT_CENTER_OFFSET from the top
      grabOffsetX: e.clientX - (nodeRect.left + nodeRect.width / 2),
      grabOffsetY: e.clientY - (nodeRect.top + remPx() / 2),
      containerLeft: containerRect?.left ?? 0,
      containerTop: containerRect?.top ?? 0,
      moved: false,
    };
    draggingIdRef.current = nodeId;
    setDraggingId(nodeId);
  }, []);

  const handlePointerMove = React.useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    drag.pointerX = e.clientX;
    drag.pointerY = e.clientY;
    if (!drag.moved && Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) > TAP_SLOP_PX) {
      drag.moved = true;
    }
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (containerRect) {
      drag.containerLeft = containerRect.left;
      drag.containerTop = containerRect.top;
    }
  }, []);

  const handlePointerUp = React.useCallback(() => {
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.captureTarget.hasPointerCapture(drag.pointerId)) {
      drag.captureTarget.releasePointerCapture(drag.pointerId);
    }
    // A press without travel is a tap: toggles selection on touch / compact,
    // where hover never fires (pointerdown's preventDefault drops compat mouse events).
    const isTap = !drag.moved && (compact || drag.pointerType !== "mouse");
    if (isTap) {
      setActiveNode((prev) => (prev === drag.nodeId ? null : drag.nodeId));
    }
    dragRef.current = null;
    draggingIdRef.current = null;
    setDraggingId(null);
  }, [compact]);

  // Tapping outside the graph clears a tapped selection
  React.useEffect(() => {
    if (!activeNode || !compact) return;
    const onDocPointerDown = (e: PointerEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setActiveNode(null);
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [activeNode, compact]);

  const setNodeRef = React.useCallback((id: string, el: HTMLElement | null) => {
    if (el) nodesRef.current.set(id, el);
    else nodesRef.current.delete(id);
  }, []);
  const setLineRef = React.useCallback((key: string, el: SVGLineElement | null) => {
    if (el) linesRef.current.set(key, el);
    else linesRef.current.delete(key);
  }, []);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () => {
      const rect = container.getBoundingClientRect();
      layoutRef.current = { width: rect.width, height: rect.height };
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  const isHighlighted = React.useCallback(
    (nodeId: string) => {
      if (!activeNode) return true;
      if (nodeId === activeNode) return true;
      return graph.map.get(activeNode)?.connections.includes(nodeId) ?? false;
    },
    [activeNode, graph],
  );
  const isEdgeHighlighted = React.useCallback(
    (a: string, b: string) => {
      if (!activeNode) return true;
      return activeNode === a || activeNode === b;
    },
    [activeNode],
  );

  // Desktop tooltip flips left when the node sits in the right 40% of the viewport
  const shouldFlipLeft = React.useCallback((nodeId: string) => {
    const el = nodesRef.current.get(nodeId);
    if (!el) return false;
    return el.getBoundingClientRect().right > window.innerWidth * 0.6;
  }, []);

  // ── Single rAF loop: intro + float + repulsion + drag + lines ─────────────
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const startTime = performance.now();
    let introCompletedAt = -1;
    let raf = 0;

    const calcOffset = (value: number, base: string) => {
      const sign = value < 0 ? "-" : "+";
      return `calc(${base} ${sign} ${Math.abs(value).toFixed(3)}px)`;
    };

    const tick = (now: number) => {
      const { width: W, height: H } = layoutRef.current;
      const { map, edges, states, center } = graphRef.current;
      const nodeList = Array.from(map.values());
      if (W === 0 || H === 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const elapsed = (now - startTime) / 1000;
      const nowS = now / 1000;
      const currentDragging = draggingIdRef.current;

      // 0. Intro progress
      let allIntrosDone = true;
      for (const node of nodeList) {
        const s = states.get(node.id)!;
        if (s.introStartTime < 0) {
          allIntrosDone = false;
          continue;
        }
        const localT = nowS - s.introStartTime - s.introDelay;
        if (localT <= 0) {
          s.introProgress = 0;
          s.introOpacityProgress = 0;
          s.introScaleProgress = 0;
          allIntrosDone = false;
          continue;
        }
        const raw = Math.min(localT / INTRO_DURATION_S, 1);
        if (raw >= 1) {
          s.introProgress = 1;
          s.introScaleProgress = 1;
          s.introOpacityProgress = 1;
        } else {
          s.introProgress = easeOutQuart(raw);
          s.introScaleProgress = easeOutBack(raw);
          s.introOpacityProgress = easeOutCubic(Math.max(0, (raw - 0.15) / 0.85));
          allIntrosDone = false;
        }
      }
      if (allIntrosDone) {
        if (introCompletedAt < 0) introCompletedAt = nowS;
      } else {
        introCompletedAt = -1;
      }
      let floatWeight = 0;
      if (introCompletedAt >= 0) {
        const t = Math.min(1, (nowS - introCompletedAt) / FLOAT_RAMP_S);
        floatWeight = t * t * (3 - 2 * t);
      }

      // 1. Base positions (spread from the center node)
      const basePositions = new Map<string, { xPct: number; yPct: number }>();
      for (const node of nodeList) {
        const p = states.get(node.id)!.introProgress;
        basePositions.set(node.id, {
          xPct: center.x + (node.x - center.x) * p,
          yPct: center.y + (node.y - center.y) * p,
        });
      }

      // 2. Current centers in px (last rendered offsets, no layout reads)
      const centers = new Map<string, { cx: number; cy: number }>();
      for (const node of nodeList) {
        const base = basePositions.get(node.id)!;
        const off = renderedOffsetsRef.current.get(node.id) ?? { dx: 0, dy: 0 };
        centers.set(node.id, { cx: (base.xPct / 100) * W + off.dx, cy: (base.yPct / 100) * H + off.dy });
      }

      // 3. Repulsion once the intro settled
      if (allIntrosDone) {
        for (let a = 0; a < nodeList.length; a++) {
          for (let b = a + 1; b < nodeList.length; b++) {
            const nA = nodeList[a];
            const nB = nodeList[b];
            const cA = centers.get(nA.id)!;
            const cB = centers.get(nB.id)!;
            const dx = cB.cx - cA.cx;
            const dy = cB.cy - cA.cy;
            const dist = Math.hypot(dx, dy);
            if (dist < REPEL_RADIUS && dist > 1) {
              const force = ((REPEL_RADIUS - dist) / REPEL_RADIUS) * REPEL_STRENGTH;
              const impulse = force * 0.05 * floatWeight;
              const nx = dx / dist;
              const ny = dy / dist;
              if (nB.id !== currentDragging) {
                const sB = states.get(nB.id)!;
                sB.vx += nx * impulse;
                sB.vy += ny * impulse;
              }
              if (nA.id !== currentDragging) {
                const sA = states.get(nA.id)!;
                sA.vx -= nx * impulse;
                sA.vy -= ny * impulse;
              }
            }
          }
        }
        for (const node of nodeList) {
          if (node.id === currentDragging) continue;
          const s = states.get(node.id)!;
          s.repelDx += s.vx;
          s.repelDy += s.vy;
          s.vx *= FRICTION;
          s.vy *= FRICTION;
          s.repelDx *= 0.97;
          s.repelDy *= 0.97;
          if (Math.abs(s.vx) < 0.01) s.vx = 0;
          if (Math.abs(s.vy) < 0.01) s.vy = 0;
          if (Math.abs(s.repelDx) < 0.05 && Math.abs(s.repelDy) < 0.05 && s.vx === 0 && s.vy === 0) {
            s.repelDx = 0;
            s.repelDy = 0;
          }
        }
      }

      // 4. Write transforms
      const finalCenters = new Map<string, { cx: number; cy: number }>();
      const active = activeNodeRef.current;
      for (const node of nodeList) {
        const el = nodesRef.current.get(node.id);
        if (!el) continue;
        const s = states.get(node.id)!;
        const base = basePositions.get(node.id)!;
        const isCenter = node.id === centerId;

        let floatX = 0;
        let floatY = 0;
        if (!isCenter && floatWeight > 0) {
          const amp = FLOAT_AMP * floatWeight;
          floatX = Math.sin(elapsed * s.floatSpeed + s.floatPhase) * amp;
          floatY = Math.cos(elapsed * s.floatSpeed * 0.7 + s.floatPhase + 1) * amp;
        }

        const baseCenterX = (base.xPct / 100) * W;
        const baseCenterY = (base.yPct / 100) * H;
        const drag = dragRef.current?.nodeId === node.id ? dragRef.current : null;
        if (drag) {
          const desiredX = drag.pointerX - drag.grabOffsetX - drag.containerLeft;
          const desiredY = drag.pointerY - drag.grabOffsetY - drag.containerTop;
          s.dragDx = desiredX - baseCenterX - floatX - s.repelDx;
          s.dragDy = desiredY - baseCenterY - floatY - s.repelDy;
        }
        const totalDx = s.dragDx + s.repelDx + floatX;
        const totalDy = s.dragDy + s.repelDy + floatY;

        const highlighted = !active || node.id === active || (map.get(active)?.connections.includes(node.id) ?? false);
        const opacity = Math.min(1, s.introOpacityProgress) * (highlighted ? 1 : 0.15);
        const blur = (1 - Math.min(1, s.introOpacityProgress)) * 16 + (highlighted ? 0 : 2);
        const scale = isCenter ? 1 : 0.6 + Math.min(1, s.introScaleProgress) * 0.4;

        el.style.opacity = String(opacity);
        el.style.filter = blur > 0.1 ? `blur(${blur}px)` : "";
        const dx = ((base.xPct - 50) / 100) * W + totalDx;
        const dy = ((base.yPct - 50) / 100) * H + totalDy;
        el.style.transform = `translate3d(${calcOffset(dx, "-50%")}, ${calcOffset(dy, `-${DOT_CENTER_OFFSET}`)}, 0) scale(${scale})`;

        finalCenters.set(node.id, { cx: baseCenterX + totalDx, cy: baseCenterY + totalDy });
        renderedOffsetsRef.current.set(node.id, { dx: totalDx, dy: totalDy });
      }

      // 5. Lines (px units, no viewBox) — drawn dot to dot
      for (const [aId, bId] of edges) {
        const line = linesRef.current.get(`${aId}__${bId}`);
        const a = finalCenters.get(aId);
        const b = finalCenters.get(bId);
        if (!a || !b || !line) continue;
        line.setAttribute("x1", a.cx.toFixed(2));
        line.setAttribute("y1", a.cy.toFixed(2));
        line.setAttribute("x2", b.cx.toFixed(2));
        line.setAttribute("y2", b.cy.toFixed(2));
        if (!active) {
          const pA = states.get(aId)?.introOpacityProgress ?? 0;
          const pB = states.get(bId)?.introOpacityProgress ?? 0;
          line.style.opacity = String(Math.min(1, pA, pB));
        }
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [centerId]);

  // Compact tooltip placement from the node's resting position so it never
  // leaves the graph box horizontally.
  const compactTooltipClass = (node: EmotionNodeData) =>
    cn(
      "absolute w-[34rem] max-w-[calc(100vw-4rem)]",
      node.y > 60 ? "bottom-full mb-[1.5rem] origin-bottom" : "top-full mt-[1.5rem] origin-top",
      node.x < 35 ? "left-0" : node.x > 65 ? "right-0" : "left-1/2 -translate-x-1/2",
    );

  return (
    <div
      ref={containerRef}
      className={cn("relative w-full overflow-visible", className)}
      style={{ aspectRatio }}>
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
        aria-hidden="true">
        <title>Connection lines between nodes</title>
        {graph.edges.map(([aId, bId]) => {
          const lit = isEdgeHighlighted(aId, bId);
          return (
            <line
              key={`${aId}__${bId}`}
              ref={(el) => setLineRef(`${aId}__${bId}`, el)}
              x1={0}
              y1={0}
              x2={0}
              y2={0}
              stroke={lit && activeNode ? "rgba(255,255,255,1)" : "rgba(255,255,255,0.55)"}
              strokeWidth="1"
              strokeDasharray={lit && activeNode ? "none" : "4 3"}
              style={{ opacity: lit ? 1 : 0.3, transition: "opacity 0.3s ease, stroke-dasharray 0.3s ease" }}
            />
          );
        })}
      </svg>

      {nodes.map((node) => {
        const highlighted = isHighlighted(node.id);
        const isActive = activeNode === node.id;
        const isDragging = draggingId === node.id;
        const isCenter = node.id === centerId;
        return (
          <button
            type="button"
            key={node.id}
            ref={(el) => setNodeRef(node.id, el)}
            aria-pressed={isActive}
            aria-label={isCenter ? "You" : node.label}
            className={cn(
              "group absolute flex flex-col items-center gap-[1.5rem] border-none bg-transparent p-0 outline-none touch-none",
              isDragging ? "cursor-grabbing" : "cursor-grab",
            )}
            style={{
              left: "50%",
              top: "50%",
              transform: `translate3d(-50%, -${DOT_CENTER_OFFSET}, 0)`,
              opacity: 0,
              zIndex: isDragging ? 30 : isActive ? 20 : isCenter ? 15 : 10,
              willChange: "transform, opacity, filter",
            }}
            onPointerDown={(e) => handlePointerDown(e, node.id)}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onMouseEnter={() => {
              if (!compact && !draggingIdRef.current) setActiveNode(node.id);
            }}
            onMouseLeave={() => {
              if (!compact && !draggingIdRef.current) setActiveNode(null);
            }}
            onFocus={() => {
              if (!compact) setActiveNode(node.id);
            }}
            onBlur={() => {
              if (!compact) setActiveNode(null);
            }}>
            <span
              aria-hidden="true"
              className="block h-[1rem] w-[1rem] shrink-0 bg-white"
            />
            {isCenter ? (
              <span className="flex h-[5.75rem] w-[5.75rem] items-center justify-center bg-white">
                <Image
                  src={humanDeco}
                  alt=""
                  className="h-[4.5rem] w-[4.5rem]"
                />
              </span>
            ) : (
              <span
                className={cn(
                  "block select-none whitespace-nowrap bg-white px-[1rem] py-[0.25rem] font-spacemono text-[1.75rem] uppercase leading-[140%] transition-colors duration-300",
                  isActive ? "text-[#F50]" : "text-black",
                )}>
                {node.label}
              </span>
            )}

            <AnimatePresence>
              {isActive && !isDragging && node.description && (
                <span
                  className={cn(
                    "pointer-events-none block",
                    compact
                      ? compactTooltipClass(node)
                      : cn(
                          "absolute top-0 w-max max-w-[32rem]",
                          shouldFlipLeft(node.id) ? "right-full mr-[1rem]" : "left-full ml-[1rem]",
                        ),
                  )}
                  style={{ zIndex: 30 }}>
                  <motion.span
                    initial={{ opacity: 0, scale: 0.9, y: 6, filter: "blur(6px)" }}
                    animate={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, scale: 0.92, y: 4, filter: "blur(4px)" }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="block rounded-[0.5rem] border border-white/20 bg-white p-[1.5rem] text-start backdrop-blur-sm">
                    <span className="m-0 line-clamp-4 block text-[2rem] leading-[150%] text-black">{node.description}</span>
                  </motion.span>
                </span>
              )}
            </AnimatePresence>
          </button>
        );
      })}
    </div>
  );
}
